import { PillIconMeta, PillTextAlign, PillTextbox } from '../types';
import { resizePillToFit } from './resize-pill';

// Text content

export const getPillText = (p: PillTextbox): string => p.text ?? '';

export const setPillText = (p: PillTextbox, text: string): void => {
  // Pills don't use per-character styles — uniform fontWeight/fontFamily across the whole
  // text. setTextPreservingStyles (used by AutoFitText) is unnecessary here.
  p.set({ text });
  resizePillToFit(p);
};

// Colors

export const getPillFill = (p: PillTextbox): string => p.pill.fill;

export const setPillFill = (p: PillTextbox, color: string): void => {
  p.pill.fill = color;
  p.dirty = true;
  p.canvas?.requestRenderAll();
};

export const getPillTextFill = (p: PillTextbox): string => (p.fill as string) ?? '';

export const setPillTextFill = (p: PillTextbox, color: string): void => {
  p.set('fill', color);
  p.canvas?.requestRenderAll();
};

// Typography

export const getPillFontFamily = (p: PillTextbox): string => p.fontFamily ?? '';

export const setPillFontFamily = (p: PillTextbox, value: string): void => {
  p.set('fontFamily', value);
  resizePillToFit(p);
};

export const getPillFontSize = (p: PillTextbox): number => p.fontSize ?? 16;

export const setPillFontSize = (p: PillTextbox, value: number): void => {
  p.set('fontSize', value);
  resizePillToFit(p);
};

export const getPillFontWeight = (p: PillTextbox): number | string => p.fontWeight ?? 400;

export const setPillFontWeight = (p: PillTextbox, value: number | string): void => {
  p.set('fontWeight', value);
  resizePillToFit(p);
};

export const getPillFontStyle = (p: PillTextbox): 'normal' | 'italic' =>
  (p.fontStyle as 'normal' | 'italic') ?? 'normal';

export const setPillFontStyle = (p: PillTextbox, value: 'normal' | 'italic'): void => {
  p.set('fontStyle', value);
  resizePillToFit(p);
};

export const getPillTextAlign = (p: PillTextbox): PillTextAlign => (p.textAlign as PillTextAlign) ?? 'center';

export const setPillTextAlign = (p: PillTextbox, value: PillTextAlign): void => {
  p.set('textAlign', value);
  resizePillToFit(p);
};

// Shape

export const getPillBorderRadius = (p: PillTextbox): number => p.pill.borderRadius;

export const setPillBorderRadius = (p: PillTextbox, value: number): void => {
  p.pill.borderRadius = value;
  p.dirty = true;
  p.canvas?.requestRenderAll();
};

// Border

export const getPillStroke = (p: PillTextbox): string => p.pill.stroke ?? '#000000';

export const setPillStroke = (p: PillTextbox, color: string): void => {
  p.pill.stroke = color;
  p.dirty = true;
  p.canvas?.requestRenderAll();
};

export const getPillStrokeWidth = (p: PillTextbox): number => p.pill.strokeWidth ?? 0;

export const setPillStrokeWidth = (p: PillTextbox, value: number): void => {
  p.pill.strokeWidth = value;
  p.dirty = true;
  p.canvas?.requestRenderAll();
};

// Size & padding

export const getPillPaddingX = (p: PillTextbox): number => p.pill.paddingX;

export const setPillPaddingX = (p: PillTextbox, value: number): void => {
  p.pill.paddingX = value;
  resizePillToFit(p);
};

// Asymmetric side paddings — fall back to paddingX when undefined.

export const getPillPaddingLeft = (p: PillTextbox): number => p.pill.paddingLeft ?? p.pill.paddingX;

export const setPillPaddingLeft = (p: PillTextbox, value: number): void => {
  p.pill.paddingLeft = value;
  resizePillToFit(p);
};

export const getPillPaddingRight = (p: PillTextbox): number => p.pill.paddingRight ?? p.pill.paddingX;

export const setPillPaddingRight = (p: PillTextbox, value: number): void => {
  p.pill.paddingRight = value;
  resizePillToFit(p);
};

export const swapPillSidePaddings = (p: PillTextbox): void => {
  const { paddingLeft, paddingRight } = p.pill;
  p.pill.paddingLeft = paddingRight;
  p.pill.paddingRight = paddingLeft;
  resizePillToFit(p);
};

export const getPillPaddingY = (p: PillTextbox): number => p.pill.paddingY;

export const setPillPaddingY = (p: PillTextbox, value: number): void => {
  p.pill.paddingY = value;
  resizePillToFit(p);
};

export const getPillMinWidth = (p: PillTextbox): number => p.pill.minWidth;

export const setPillMinWidth = (p: PillTextbox, value: number): void => {
  p.pill.minWidth = value;
  resizePillToFit(p);
};

// Icon

export const getPillIcon = (p: PillTextbox): PillIconMeta | undefined => p.pill.icon;

export const setPillIcon = (p: PillTextbox, icon: PillIconMeta): void => {
  p.pill.icon = icon;
  resizePillToFit(p);
};

export const clearPillIcon = (p: PillTextbox): void => {
  p.pill.icon = undefined;
  resizePillToFit(p);
};
